<template>
  <div v-qa="'calculator'" :class="{ disabled, wide, full: buttonVisible, editable }" class="calculator">
    <sf-calculator-tabs :limit="limit" class="calculator__tabs"/>

    <div class="calculator__top sf-ph-s sf-pv-s">
      <sf-calculator
        ref="sliders"
        v-bind="{ mode, limit, colors }"
        class="calculator__sliders"
        :info-visible="infoVisible"
        @edit="updateEdit"/>

      <sf-slide-toggle :value="aboveAvailableAmount">
        <div class="toggle-block">
          <nuxt-link :to="loginPagePath" class="toggle-block__link">
            {{ $t('calculator.repeated-user') }}
          </nuxt-link>
        </div>
      </sf-slide-toggle>

      <slot name="button">
        <span v-if="!infoVisible" class="term-warning">{{ $t('calculator.term-warning') }}</span>
        <sf-button
          v-if="buttonVisible"
          v-qa="'btn_calculator_submit'"
          :disabled="disabled"
          :url="url"
          class="secondary calculator__button">
          <template v-if="aboveAvailableAmount">{{ $t('calculator.login-btn') }}</template>
          <template v-else>{{ $t('calculator.submit-btn') }}</template>
        </sf-button>
      </slot>
    </div>

    <div class="calculator__bottom">
      <calculator-stats v-if="statsVisible" :info-visible="infoVisible"/>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import { CALCULATOR_MODE, TAB } from '~/modules/arithmetic/utils/consts';
import { WEB_ROUTES } from '~/app/shared/utils/web-config';
import CalculatorStats from '~/components/calculator/stats';

export default {
  name: 'CalculatorBase',
  components: { CalculatorStats },
  props: {
    mode: {
      type: String,
      default: CALCULATOR_MODE.FULL,
    },
    limit: {
      type: String,
      default: '',
    },
    wide: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editable: this.mode === CALCULATOR_MODE.FULL,
      colors: {
        process: this.$theme.colors.secondary,
        arrows: this.$theme.colors.default,
        dot: this.$theme.colors.primary,
      },
    };
  },
  computed: {
    ...mapState('arithmetic', ['current']),
    ...mapGetters('arithmetic', [
      'amount',
      'disabled',
      'authorized',
      'term',
      'repeated',
      'displayed',
      'additional',
    ]),
    aboveAvailableAmount() {
      if (this.disabled || this.authorized) return false;
      return this.amount.current > this.amount.first;
    },
    tab() {
      return this.$store.state.arithmetic.current.tab;
    },
    full() {
      return this.mode === CALCULATOR_MODE.FULL;
    },
    url() {
      if (this.authorized) {
        return this.localePath(WEB_ROUTES.APPLICATION_APPLY);
      }

      if (this.tab === TAB.REPEATED) {
        return this.localePath(WEB_ROUTES.LOGIN);
      }

      return this.localePath(WEB_ROUTES.REGISTRATION_STEP_1);
    },

    loginPagePath() {
      return this.localePath(WEB_ROUTES.LOGIN);
    },

    routeName() {
      return this.$route.name;
    },

    buttonVisible() {
      const isNotRegisterPage = ![
        WEB_ROUTES.REGISTRATION_STEP_1,
        WEB_ROUTES.REGISTRATION_STEP_2,
        WEB_ROUTES.REGISTRATION_STEP_3,
        WEB_ROUTES.REGISTRATION_STEP_4,
        WEB_ROUTES.APPLICATION_APPLY,
      ].includes(`${this.routeName}`.split('___')[0]);

      return isNotRegisterPage && this.full;
    },

    infoVisible() {
      return ![
        WEB_ROUTES.MAIN,
        WEB_ROUTES.REGISTRATION_STEP_1,
        WEB_ROUTES.REGISTRATION_STEP_2,
        WEB_ROUTES.REGISTRATION_STEP_3,
        WEB_ROUTES.REGISTRATION_UPLOAD,
        WEB_ROUTES.HOW_TO_PAY,
        WEB_ROUTES.HOW_TO_GET,
        WEB_ROUTES.FAQ,
        WEB_ROUTES.PROFILE_NO_LOAN,
        WEB_ROUTES.ABOUT,
      ].includes(`${this.routeName}`.split('___')[0]);
    },

    statsVisible() {
      const applyPages = [WEB_ROUTES.REGISTRATION_STEP_4, WEB_ROUTES.APPLICATION_APPLY];
      const isApplyPage = applyPages.some(page => this.$route.name.includes(page));

      if (isApplyPage && !this.additional) {
        return this.current.term !== 0;
      }

      return true;
    },
  },
  watch: {
    aboveAvailableAmount(value) {
      this.setValue({ key: 'tab', value: value ? TAB.REPEATED : TAB.FIRST });
    },
  },
  methods: {
    ...mapMutations('arithmetic', ['setValue']),
    updateEdit(editable) {
      this.editable = !!editable;
    },
    refresh() {
      this.$refs.sliders.refresh();
    },
  },
};
</script>

<style lang="scss" scoped>
$bg-top: map-get($colors, default);
$color-top: #fff;
$bg-bottom: #efefef;

.calculator {
  position: relative;
  background: $bg-top;
  outline: none;
  overflow: hidden;
  box-shadow: -2px 4px 20px rgba(#000, 0.15);
  width: 375px;
  @include tablet {
    border-radius: 10px;
  }
  @include mobile {
    width: 100%;
    display: inline-block;
  }
  &.wide {
    @include not-desktop {
    }
  }

  &.disabled {
    &:after {
      @extend %overlay;
      position: absolute;
      z-index: 1;
      background: rgba(#fff, 0.5);
      content: '';

      @include tablet {
        border-radius: 10px;
      }
    }
  }

  &.full {
    .calculator__top {
      padding-bottom: map-get($paddings, normal);
    }

    .calculator__stats {
      padding-top: 35px;
      padding-bottom: 15px;
    }
  }

  &:not(.full) {
    .calculator__tooltip {
      display: none;
    }
  }

  &:not(.editable) {
    @include mobile {
      .calculator__bottom {
        display: none;
      }
      .calculator__stats {
        display: none;
      }
    }
  }

  &__top {
    position: relative;
    font-size: 14px;
  }

  &__tabs {
    font-size: 14px;
  }

  &__bottom {
    background: $bg-bottom;
  }

  &__button {
    position: absolute;
    bottom: 0;
    z-index: 5;
    transform: translateY(50%);

    @include mobile {
      left: map-get($paddings, smallM);
      right: map-get($paddings, smallM);
    }

    @include tablet {
      left: map-get($paddings, small);
      right: map-get($paddings, small);
    }
  }

  /deep/ .edit {
    padding: 0;
  }
}

.toggle-block {
  margin: map-get($margins, small) auto map-get($margins, xsmall);
  text-align: center;
  &__link {
    color: inherit;
  }
}

.term-warning {
  color: map-get($colors, text);
}
</style>
